import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Copy,
  Check,
  History,
  Trash2,
  Loader2,
  AlertTriangle,
  Mail,
  CheckCircle2,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../components/ui/tabs";
import { useConfirm } from "../components/ConfirmDialog";
import { TEMPLATES, SUBJECTS, renderTemplate } from "../lib/b2b-email-templates";

function copyToClipboard(text) {
  // navigator.clipboard fails on insecure contexts → fallback textarea
  if (navigator?.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      resolve();
    } catch (e) {
      reject(e);
    }
  });
}

const emptyForm = {
  prenom: "",
  enseigne: "",
  ville: "",
  email: "",
  notes: "",
};

const TEMPLATE_KEYS = Object.keys(TEMPLATES);

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

function fmtDaysAgo(days) {
  if (days == null) return "";
  if (days === 0) return "aujourd'hui";
  if (days === 1) return "hier";
  if (days < 30) return `il y a ${days} jours`;
  if (days < 365) return `il y a ${Math.floor(days / 30)} mois`;
  return `il y a ${Math.floor(days / 365)} an${days >= 730 ? "s" : ""}`;
}

export default function EmailProspection() {
  const confirm = useConfirm();
  const [form, setForm] = useState(emptyForm);
  const [variant, setVariant] = useState("first_contact");
  const [selectedSubject, setSelectedSubject] = useState(0);
  const [copiedHTML, setCopiedHTML] = useState(false);
  const [copiedSubject, setCopiedSubject] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, week: 0, month: 0 });
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [submittingLog, setSubmittingLog] = useState(false);
  // Phase 101ch (18/02/2026) — Accroche prospection éditable via Paramètres.
  const [intro, setIntro] = useState("");

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data } = await api.get("/admin/b2b-outreach-logs");
      setLogs(data.logs || []);
      setStats(data.stats || { total: 0, week: 0, month: 0 });
    } catch (e) {
      toast.error("Erreur de chargement de l'historique");
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // Phase 101ch — Charge l'accroche prospection depuis Paramètres pour l'injecter
    // dans les templates. Fallback silencieux si non défini.
    api.get("/settings").then(({ data }) => {
      setIntro(data?.b2b_outreach_intro || "");
    }).catch(() => { /* défaut restera "" → fallback dans renderTemplate */ });
  }, []);

  // Détection doublons live (email OU business_name)
  useEffect(() => {
    const email = form.email.trim().toLowerCase();
    const enseigne = form.enseigne.trim();
    if (!email && !enseigne) {
      setDuplicateInfo(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (email) params.set("email", email);
        else if (enseigne) params.set("business_name", enseigne);
        const { data } = await api.get(`/admin/b2b-outreach-logs/duplicate-check?${params}`);
        setDuplicateInfo(data?.found ? data : null);
      } catch {
        setDuplicateInfo(null);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [form.email, form.enseigne]);

  // HTML to copy/send → garde l'URL de production (maison-thai.ch) pour le destinataire.
  const sendHTML = useMemo(() => {
    return renderTemplate(TEMPLATES[variant].html, { ...form, intro });
  }, [variant, form, intro]);

  // HTML pour l'aperçu → réécrit l'URL des assets vers l'origine courante,
  // pour que l'aperçu s'affiche correctement avant le déploiement en prod.
  const previewHTML = useMemo(() => {
    if (typeof window === "undefined") return sendHTML;
    return sendHTML.replaceAll(
      "https://maison-thai.ch/logo-maisonthai-name.png",
      `${window.location.origin}/logo-maisonthai-name.png`
    );
  }, [sendHTML]);

  const subjectsForVariant = SUBJECTS[variant] || [];
  const currentSubjectRaw = subjectsForVariant[selectedSubject] || "";
  const currentSubject = useMemo(
    () => renderTemplate(currentSubjectRaw, { ...form, intro }),
    [currentSubjectRaw, form, intro]
  );

  const canCopy = form.prenom.trim() && form.enseigne.trim();

  const doCopyHTML = async () => {
    if (!canCopy) {
      toast.error("Renseignez au moins Prénom et Enseigne");
      return;
    }
    try {
      await copyToClipboard(sendHTML);
      setCopiedHTML(true);
      toast.success("HTML copié — collez-le dans Mailspring (Format → HTML)");
      setTimeout(() => setCopiedHTML(false), 2500);
    } catch (e) {
      toast.error("Impossible de copier dans le presse-papiers");
    }
  };

  const doCopySubject = async (text) => {
    try {
      await copyToClipboard(text);
      setCopiedSubject(true);
      toast.success("Sujet copié");
      setTimeout(() => setCopiedSubject(false), 2000);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const doLogSent = async () => {
    if (!canCopy) {
      toast.error("Renseignez au moins Prénom et Enseigne avant de tracer l'envoi");
      return;
    }
    setSubmittingLog(true);
    try {
      await api.post("/admin/b2b-outreach-logs", {
        contact_name: form.prenom.trim(),
        business_name: form.enseigne.trim(),
        email: form.email.trim() || null,
        city: form.ville.trim() || null,
        template_variant: variant,
        subject_used: currentSubject || null,
        notes: form.notes.trim() || null,
      });
      toast.success(`Envoi tracé : ${form.enseigne}`);
      setForm(emptyForm);
      setDuplicateInfo(null);
      await loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de journalisation");
    } finally {
      setSubmittingLog(false);
    }
  };

  // Phase 101ci (18/02/2026) — Envoi direct via Resend (bypass copier-coller
  // Thunderbird/Mailspring). Le backend envoie ET auto-log en une seule requête.
  const [sendingDirect, setSendingDirect] = useState(false);
  const doSendDirect = async () => {
    if (!canCopy) {
      toast.error("Renseignez au moins Prénom et Enseigne");
      return;
    }
    const email = form.email.trim();
    if (!email) {
      toast.error("Email du destinataire obligatoire pour l'envoi direct");
      return;
    }
    const ok = await confirm({
      title: `Envoyer directement à ${email} ?`,
      description: `L'email sera envoyé via Resend (from: noreply@send.maison-thai.ch, reply-to: info@maison-thai.ch) et l'envoi sera tracé automatiquement dans l'historique. Sujet : « ${currentSubject} ».`,
      confirmText: "Envoyer maintenant",
      cancelText: "Annuler",
    });
    if (!ok) return;
    setSendingDirect(true);
    try {
      const { data } = await api.post("/admin/b2b-outreach/send", {
        to_email: email,
        subject: currentSubject,
        html: sendHTML,
        contact_name: form.prenom.trim(),
        business_name: form.enseigne.trim(),
        city: form.ville.trim() || "",
        template_variant: variant,
        notes: form.notes.trim() || null,
      });
      toast.success(data.message || "Email envoyé");
      setForm(emptyForm);
      setDuplicateInfo(null);
      await loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi échoué — copiez le HTML et utilisez Mailspring/Thunderbird");
    } finally {
      setSendingDirect(false);
    }
  };

  const deleteLog = async (log) => {
    const ok = await confirm({
      title: "Supprimer cet envoi ?",
      description: `L'entrée « ${log.business_name} » du ${fmtDateTime(log.sent_at)} sera supprimée de l'historique. Action irréversible.`,
      destructive: true,
      confirmText: "Supprimer",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/b2b-outreach-logs/${log.id}`);
      toast.success("Entrée supprimée");
      await loadLogs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="space-y-6" data-testid="email-prospection-page">
      <div>
        <Link to="/parametres" className="text-xs text-[#575A57] hover:text-[#1F4E3D] inline-flex items-center gap-1.5">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour aux paramètres
        </Link>
        <div className="mt-2 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-heading text-[#1A1C1A] flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#1F4E3D]" /> Prospection B2B manuelle
            </h1>
            <p className="text-sm text-[#575A57] mt-1">
              Personnalisez un email ciblé, copiez le HTML dans <strong>Mailspring</strong>, envoyez, puis tracez l'envoi.
              Pas de campagne masse — un prospect à la fois, sélectionné par vous.
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Cette semaine</div>
            <div className="font-mono text-2xl text-[#1A1C1A] mt-1" data-testid="stat-week">{stats.week}</div>
            <div className="text-[10px] text-[#A3A6A3] mt-1">emails envoyés (7 derniers jours)</div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Ce mois</div>
            <div className="font-mono text-2xl text-[#1A1C1A] mt-1" data-testid="stat-month">{stats.month}</div>
            <div className="text-[10px] text-[#A3A6A3] mt-1">emails envoyés (30 derniers jours)</div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E5E0]">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-[#A3A6A3]">Total cumulé</div>
            <div className="font-mono text-2xl text-[#1F4E3D] mt-1" data-testid="stat-total">{stats.total}</div>
            <div className="text-[10px] text-[#A3A6A3] mt-1">prospects contactés au total</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ============ COLONNE GAUCHE — Formulaire ============ */}
        <div className="lg:col-span-5 space-y-4">
          <Card className="border-[#E5E5E0]">
            <CardContent className="p-5 space-y-4">
              <div className="text-xs uppercase tracking-wider text-[#1F4E3D] font-semibold flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Personnalisation
              </div>

              <div>
                <Label className="mb-1.5 block">Prénom du contact *</Label>
                <Input
                  value={form.prenom}
                  onChange={(e) => setForm({ ...form, prenom: e.target.value })}
                  placeholder="Pierre"
                  data-testid="form-prenom"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block">Enseigne *</Label>
                  <Input
                    value={form.enseigne}
                    onChange={(e) => setForm({ ...form, enseigne: e.target.value })}
                    placeholder="Coop Pronto"
                    data-testid="form-enseigne"
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block">Ville</Label>
                  <Input
                    value={form.ville}
                    onChange={(e) => setForm({ ...form, ville: e.target.value })}
                    placeholder="Sion"
                    data-testid="form-ville"
                  />
                </div>
              </div>

              <div>
                <Label className="mb-1.5 block">Email du contact</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="pierre.dupont@example.ch"
                  data-testid="form-email"
                />
                <p className="text-[10px] text-[#A3A6A3] mt-1">
                  Utilisé uniquement pour la dedup d'historique — n'est PAS inséré dans le HTML.
                </p>
              </div>

              <div>
                <Label className="mb-1.5 block">Notes internes</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Ex: rencontré au salon SIRHA 2026 — chef d'achat catégorie ambient"
                  rows={2}
                  data-testid="form-notes"
                />
              </div>

              {/* Phase 101ch — Édition rapide de l'accroche prospection.
                  Sauvegarde persistée dans Paramètres.b2b_outreach_intro dès le blur. */}
              <div>
                <Label className="mb-1.5 block flex items-center gap-1.5">
                  Accroche « Nous sommes … »
                  <span className="text-[10px] font-normal text-[#A3A6A3]">— apparaîtra dans tous les emails</span>
                </Label>
                <Textarea
                  value={intro}
                  onChange={(e) => setIntro(e.target.value)}
                  onBlur={async () => {
                    try {
                      await api.put("/settings", { b2b_outreach_intro: intro });
                      toast.success("Accroche sauvegardée");
                    } catch (e) {
                      toast.error("Sauvegarde échouée");
                    }
                  }}
                  placeholder="restaurant familial établi dans ce beau Valais depuis plus de 20 ans"
                  rows={2}
                  maxLength={200}
                  data-testid="form-intro"
                />
                <p className="text-[10px] text-[#A3A6A3] mt-1">
                  Rendu : « Nous sommes <strong>Maison Thaï Conthey</strong>, {intro || "cuisine artisanale valaisanne"}. »
                </p>
              </div>

              {/* Alerte doublon */}
              {duplicateInfo && (
                <div
                  className="p-3 rounded border bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F] flex items-start gap-2"
                  data-testid="duplicate-alert"
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div className="text-xs leading-relaxed">
                    <strong>Déjà contacté</strong>{" "}
                    {fmtDaysAgo(duplicateInfo.days_ago)} &mdash;{" "}
                    {duplicateInfo.last_contact?.business_name}
                    {duplicateInfo.last_contact?.city ? ` (${duplicateInfo.last_contact.city})` : ""}.
                    Variante : <em>{TEMPLATES[duplicateInfo.last_contact?.template_variant]?.label || duplicateInfo.last_contact?.template_variant}</em>.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-[#E5E5E0]">
            <CardContent className="p-5 space-y-4">
              <div className="text-xs uppercase tracking-wider text-[#1F4E3D] font-semibold">
                Template
              </div>
              <Tabs value={variant} onValueChange={(v) => { setVariant(v); setSelectedSubject(0); }}>
                <TabsList className="grid grid-cols-2 w-full">
                  {TEMPLATE_KEYS.map((k) => (
                    <TabsTrigger key={k} value={k} data-testid={`template-tab-${k}`}>
                      {TEMPLATES[k].label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {TEMPLATE_KEYS.map((k) => (
                  <TabsContent key={k} value={k} className="mt-3">
                    <p className="text-xs text-[#575A57] leading-relaxed">
                      {TEMPLATES[k].description}
                    </p>
                  </TabsContent>
                ))}
              </Tabs>

              <div>
                <div className="text-xs uppercase tracking-wider text-[#575A57] font-semibold mb-2">
                  Sujets disponibles (3 variantes A/B/C)
                </div>
                <div className="space-y-2">
                  {subjectsForVariant.map((s, i) => {
                    const rendered = renderTemplate(s, form);
                    const selected = i === selectedSubject;
                    return (
                      <div
                        key={i}
                        className={`flex items-center gap-2 p-2.5 rounded border text-xs transition-all cursor-pointer ${
                          selected
                            ? "border-[#1F4E3D] bg-[#EAF4EE]"
                            : "border-[#E5E5E0] bg-white hover:border-[#1F4E3D]/30"
                        }`}
                        onClick={() => setSelectedSubject(i)}
                        data-testid={`subject-option-${i}`}
                      >
                        <div className="font-mono text-[10px] text-[#A3A6A3] w-4">{["A", "B", "C"][i]}</div>
                        <div className="flex-1 text-[#1A1C1A]">{rendered}</div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); doCopySubject(rendered); }}
                          className="h-7 w-7 shrink-0"
                          title="Copier ce sujet"
                          data-testid={`copy-subject-${i}`}
                        >
                          {copiedSubject ? <Check className="w-3.5 h-3.5 text-[#1F4E3D]" /> : <Copy className="w-3.5 h-3.5" />}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Actions envoyer / copier + tracer */}
              <div className="space-y-2 pt-2 border-t border-[#E5E5E0]">
                {/* Phase 101ci — Bouton principal : envoi direct via Resend */}
                <Button
                  onClick={doSendDirect}
                  disabled={!canCopy || !form.email.trim() || sendingDirect}
                  className="w-full bg-[#C05C47] hover:bg-[#A94E3B] text-white"
                  data-testid="send-direct-btn"
                  title={!form.email.trim() ? "Renseignez d'abord l'email du destinataire" : `Envoyer directement à ${form.email}`}
                >
                  {sendingDirect ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi en cours…</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Envoyer directement (Resend)</>
                  )}
                </Button>
                <details className="rounded border border-[#E5E5E0] bg-white">
                  <summary className="text-xs text-[#575A57] px-3 py-2 cursor-pointer hover:bg-[#FAFAF7]">
                    Alternative : envoi manuel via Mailspring / Thunderbird
                  </summary>
                  <div className="p-3 space-y-2">
                    <Button
                      onClick={doCopyHTML}
                      disabled={!canCopy}
                      variant="outline"
                      className="w-full"
                      data-testid="copy-html-btn"
                    >
                      {copiedHTML ? (
                        <><Check className="w-4 h-4 mr-2" /> HTML copié</>
                      ) : (
                        <><Copy className="w-4 h-4 mr-2" /> Copier le HTML</>
                      )}
                    </Button>
                    <Button
                      onClick={doLogSent}
                      disabled={!canCopy || submittingLog}
                      variant="outline"
                      className="w-full border-[#1F4E3D]/30 text-[#1F4E3D] hover:bg-[#EAF4EE]"
                      data-testid="log-sent-btn"
                    >
                      {submittingLog ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement…</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> J&apos;ai envoyé — tracer dans l&apos;historique</>
                      )}
                    </Button>
                    <p className="text-[10px] text-[#A3A6A3] leading-relaxed">
                      Workflow manuel : 1) Copiez le HTML · 2) Collez dans Mailspring/Thunderbird · 3) Envoyez · 4) Cliquez « J&apos;ai envoyé »
                    </p>
                  </div>
                </details>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ============ COLONNE DROITE — Preview ============ */}
        <div className="lg:col-span-7 space-y-4">
          <Card className="border-[#E5E5E0]">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#1F4E3D] font-semibold">
                    Aperçu de l'email
                  </div>
                  <div className="text-[11px] text-[#A3A6A3] mt-0.5">
                    Sujet : <strong className="text-[#1A1C1A] font-mono">{currentSubject || "(renseignez les variables)"}</strong>
                  </div>
                </div>
                {!canCopy && (
                  <Badge variant="outline" className="text-[10px] bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]">
                    Variables manquantes
                  </Badge>
                )}
              </div>
              <div
                className="border border-[#E5E5E0] rounded overflow-hidden bg-[#F2ECE4]"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
                data-testid="email-preview"
              >
                <iframe
                  title="Aperçu email"
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"></head><body style="margin:0;padding:0;background:#F2ECE4;">${previewHTML}</body></html>`}
                  style={{ width: "100%", height: "70vh", border: "none", background: "#F2ECE4" }}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ============ HISTORIQUE ============ */}
      <Card className="border-[#E5E5E0]">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-4 h-4 text-[#1F4E3D]" />
            <h2 className="font-heading text-lg text-[#1A1C1A]">Historique des envois</h2>
            <Badge variant="outline" className="text-[10px] ml-2">{logs.length}</Badge>
          </div>

          {loadingLogs ? (
            <div className="flex items-center gap-2 text-xs text-[#575A57] p-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
            </div>
          ) : logs.length === 0 ? (
            <div className="text-xs text-[#A3A6A3] border border-dashed border-[#E5E5E0] rounded p-6 text-center">
              Aucun envoi tracé. Cliquez sur <strong>« J'ai envoyé »</strong> après votre premier email pour démarrer votre pipeline.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="outreach-history-table">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F0]">
                  <tr>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wide font-semibold text-[#575A57]">Date</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wide font-semibold text-[#575A57]">Contact</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wide font-semibold text-[#575A57]">Enseigne · Ville</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wide font-semibold text-[#575A57]">Email</th>
                    <th className="text-left px-3 py-2 text-xs uppercase tracking-wide font-semibold text-[#575A57]">Variante</th>
                    <th className="text-right px-3 py-2 text-xs uppercase tracking-wide font-semibold text-[#575A57]"></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7]" data-testid={`outreach-row-${log.id}`}>
                      <td className="px-3 py-2 text-xs font-mono text-[#575A57]">{fmtDateTime(log.sent_at)}</td>
                      <td className="px-3 py-2 text-[#1A1C1A]">{log.contact_name}</td>
                      <td className="px-3 py-2">
                        <span className="font-medium text-[#1A1C1A]">{log.business_name}</span>
                        {log.city && <span className="text-[#A3A6A3] text-xs ml-1.5">· {log.city}</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#575A57] font-mono">{log.email || "—"}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px]">
                          {TEMPLATES[log.template_variant]?.label || log.template_variant}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-[#C05C47]"
                          onClick={() => deleteLog(log)}
                          title="Supprimer cette entrée"
                          data-testid={`delete-outreach-${log.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
